import { RouteType } from "types/RouteType";
import { getAgentChatStore } from "./AgentChatStore";
import { OFFICE_MANAGER_NAME } from "./agentChat.types";

/**
 * AI → Rosie, HQ's office manager (formerly the isa assistant).
 *
 * A CHAT client for the one seeded internal agent (catalog row `isa-hq`, kept
 * as a wire format): read-only answers about clients, people, meetings and
 * who is privy to what, each computed by the API as the person asking.
 *
 * It is deliberately NOT an admin console over the platform's 19 services —
 * that is AI → Agent Admin (views/aiAgent/AgentAdmin), which is admin-only for
 * the reasons its own header gives. This route stays staff-visible.
 *
 * Gating: authenticated staff, NOT admin-only. She is read-only by construction
 * (her whole tool catalog is search/lookup, and the API refuses write tools),
 * and every tool reads as the person asking — so nothing here is more than
 * that person could read through HQ's own pages, and often less (the client
 * assignment gate). Private meetings reach her exactly as they reach the
 * person in HQ's screens. Provider administration —
 * the part that costs money — stays admin-only on AI → Providers / Task Routing.
 *
 * Data is loaded in the route hooks, and the transient `run.stream.*`
 * subscription is attached here and torn down on exit so a background tab is not
 * left holding socket listeners.
 */
const agentChatRoute: RouteType[] = [
  {
    name: "AgentChat",
    path: "/ai/assistant",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: OFFICE_MANAGER_NAME,
        enableRefreshButton: true,
        component: () => import("./AgentChat.Header"),
      },
      body: {
        component: () => import("./AgentChat.Body"),
        grayBackground: true,
      },
    },
    group: "AI",
    searchAliases: [
      "rosie",
      "office manager",
      "ask rosie",
      "assistant",
      "ai agent",
      "chat",
      "who can see",
      "meeting questions",
    ],
    requiresAuth: true,
    requiresAdmin: false,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      const store = getAgentChatStore(rootStore);
      store.attachRunStream();
      void store.load();
    },
    onExit: ({ rootStore }) => {
      getAgentChatStore(rootStore).dispose();
    },
  },
];

export default agentChatRoute;
